// What a tick in the DETAILED TREE (quantities) means, as pure rules the
// component and its tests share - the record-tree twin of scopeKeys.js.
//
// The two trees on this page must read the same way (owner via the leader,
// 2026-09-20: he should not have to learn which tree he is looking at), so
// the rules are the same and only the KEYS differ: Model elements ticks
// category / family / type, the Detailed tree ticks the record's own
// category / group, which the server already names `rc:C` and `rg:C|G`
// (utils/elementQuantities.groupLevel) with elements as `element:uid`.
//
// A tick means THE WHOLE BRANCH, including the children that have not
// been loaded - a group can hold a hundred thousand elements and they
// arrive 500 at a time, so what is on screen is never what is meant. Only
// the ticked node is remembered; the server resolves it (recordNodes for a
// status, the record resolver for the viewer), never the browser.
const RC = "rc:";
const RG = "rg:";
// A CUSTOM CATEGORY, ticked in this table (owner, 2026-10-05: "these 3
// categories still don't have a checkbox inside the tracking quantity table,
// they are custom categories and count parts of other categories, they need
// to behave exactly like normal categories, when it comes to operations and
// printing, and status").
//
// IT IS NOT A RECORD NODE AND CANNOT BE ONE. rc:/rg: name a place in the
// record tree and the server turns them into { rc, s, rg } - a match on
// ElementQuantity. A custom category has no record row: it is a stored
// definition whose members are PARTS, resolved against the live commit. So a
// tick on one travels as a DEFINITION ID and the server resolves it to the
// part ids, which is the only shape that can carry it.
const DERIVED = "derived:";
// A SIZE NODE (owner, 2026-10-17: "organize the conduits by sizes"): under
// the record category Conduits the tree lists trade sizes, keyed
// `rs:<category>|<material>|<size>` - `rs:Conduits|EMT|0.75`, and
// `rs:Conduits|any|unknown` for runs whose outside diameter matches no size.
// It behaves as a GROUP in every rule here (a child of its category, its
// elements below it); only what is sent to the server says size instead of
// group (back/utils/conduitSizes turns it into the match).
const RS = "rs:";

// A KEY THIS PAGE JUST BUILT must be one it knows - an unknown kind here is
// a bug in this code, and dropping it would act on a subset of what was
// ticked. So it throws. A STORED key (a saved selection, an exclusion kept
// with a set) that no longer parses is different: it is skipped and
// reported by nodesForKeys, so an old project still opens (leader,
// 2026-10-17).
export class UnknownKeyError extends Error {
  constructor(key) {
    super(`"${String(key).slice(0, 80)}" is not a kind of tree row this page knows`);
    this.key = key;
  }
}

// A group key is `rg:<category>|<group>`; a group name can contain a pipe,
// the category cannot (it is one field of the record tree), so split once.
export function parseRecordKey(key) {
  const k = String(key || "");
  if (k.startsWith(RC)) return { kind: "category", category: k.slice(RC.length), group: null };
  if (k.startsWith(RG)) {
    const rest = k.slice(RG.length);
    const at = rest.indexOf("|");
    return { kind: "group", category: at < 0 ? rest : rest.slice(0, at), group: at < 0 ? "" : rest.slice(at + 1) };
  }
  if (k.startsWith(RS)) {
    const parts = k.slice(RS.length).split("|");
    const size = parts[2] === "unknown" ? "unknown" : Number(parts[2]);
    if (parts.length !== 3 || !parts[0] || (size !== "unknown" && !(size > 0))) return { kind: "" };
    return { kind: "group", category: parts[0], material: parts[1], size, sized: true };
  }
  if (k.startsWith("element:")) return { kind: "element", uniqueId: k.slice("element:".length) };
  if (k.startsWith(DERIVED)) return { kind: "derived", derivedId: k.slice(DERIVED.length) };
  return { kind: "" };
}

// A row key in a person's words, for the confirmation ("in Cable Trays",
// "in 3/4\" EMT conduits"). A size key would otherwise read as "0.75".
export function recordKeyWords(key) {
  const p = parseRecordKey(key);
  if (p.kind === "group" && p.sized) {
    if (p.size === "unknown") return `${p.category} of unknown size`;
    const whole = Math.floor(p.size);
    const frac = ["", "1/4", "1/2", "3/4"][Math.round((p.size - whole) * 4)] || "";
    return `${whole && frac ? `${whole}-${frac}` : (whole || frac)}" ${p.material} ${p.category.toLowerCase()}`;
  }
  if (p.kind === "group") return p.group;
  if (p.kind === "category") return p.category;
  return String(key);
}

// The same, for a key the page has just built: an unknown kind throws.
export function strictRecordKey(key) {
  const p = parseRecordKey(key);
  if (!p.kind) throw new UnknownKeyError(key);
  return p;
}

export function recordParentKey(key, row = null) {
  const p = parseRecordKey(key);
  if (p.kind === "group") return `${RC}${p.category}`;
  // An element only knows its parents from the row it came from: its
  // uniqueId says nothing about the record group above it.
  if (p.kind === "element" && row && row.recordSizeKey) return row.recordSizeKey;
  if (p.kind === "element" && row && row.category) return `${RG}${row.category}|${row.group || ""}`;
  return null;
}

// The ancestors of a row, nearest first. `row` carries category/group
// because the element key cannot.
export function ancestorKeys(row) {
  if (!row) return [];
  if (row.kind === "category") return [];
  // A CUSTOM CATEGORY HAS NO ANCESTOR IN THIS TREE - it draws parts from
  // several record categories, so nothing above it can cover it. Explicit
  // rather than relying on the fall-through, because the row carries a
  // `category` field for display and an unrecognised kind reaching the
  // group branch would read it as a record category with the same name.
  if (row.kind === "derived" || row.kind === "derivedPart") return [];
  if (row.kind === "group") return [`${RC}${row.category}`];
  // An element row names the COMMIT's category/family/type; the record
  // group above it is the row it was loaded from, which the tree stamps on
  // it as recordCategory / recordGroup.
  if (row.kind === "element" && row.recordCategory !== undefined && row.recordCategory !== null) {
    // Loaded under a size node: that node is its parent, not a group.
    if (row.recordSizeKey) return [row.recordSizeKey, `${RC}${row.recordCategory}`];
    return [`${RG}${row.recordCategory}|${row.recordGroup || ""}`, `${RC}${row.recordCategory}`];
  }
  return [];
}

// EXCLUSIONS: "this category except that one" (owner, 2026-09-20). A row
// under a ticked parent can be unticked without unticking the parent - it
// becomes an EXCLUSION, carried as its own key. This is what the ticked
// child's checkbox used to refuse to do: before exclusions existed,
// unticking a child would silently have dropped the siblings nobody had
// loaded, so the box was filled and disabled. Now it does the honest
// thing instead.
export function excludedBy(row, excluded) {
  const set = excluded instanceof Set ? excluded : new Set((excluded || []).map(String));
  if (!set.size) return null;
  if (set.has(String(row.key))) return String(row.key);
  return ancestorKeys(row).find((k) => set.has(k)) || null;
}

// Ticked, or ticked by something above it: the checkbox of a row under a
// ticked category is filled, because that category really does include it.
export function checkedBy(row, checked) {
  const set = checked instanceof Set ? checked : new Set((checked || []).map(String));
  if (set.has(String(row.key))) return { checked: true, byAncestor: null };
  const up = ancestorKeys(row).find((k) => set.has(k));
  return up ? { checked: true, byAncestor: up } : { checked: false, byAncestor: null };
}

// Only the highest tick of each branch is kept: "Conduits" means the
// conduits in the model today, and the 412 groups under it would stop
// meaning that at the next publish.
export function minimalRecordKeys(keys) {
  const set = new Set((keys || []).map(String));
  return [...set].filter((k) => {
    const p = parseRecordKey(k);
    if (p.kind === "group") return !set.has(`${RC}${p.category}`);
    // A custom category is not inside any record category - it draws from
    // several - so nothing can cover it and it is always kept.
    return true;
  });
}

// A tick or an untick, with the branch below it: ticking a category drops
// the ticks inside it (they are covered), unticking removes them too.
export function toggleRecordKey(prev, row, on) {
  const key = String(row.key);
  // The row came from the tree a moment ago: an unknown kind is a bug here.
  const p = strictRecordKey(key);
  const inside = (k) => {
    if (p.kind !== "category") return false;
    const q = parseRecordKey(k);
    return q.kind === "group" && q.category === p.category;
  };
  const kept = (prev || []).map(String).filter((k) => k !== key && !inside(k));
  return on ? [...kept, key] : kept;
}

// Unticking a row that is only ticked because a parent is: the parent
// stays, and this row joins the exclusions. Ticking it again simply takes
// it back out - no exclusion is ever left behind that nothing covers.
export function toggleExclusion(prevExcluded, row, on) {
  const key = String(row.key);
  const inside = (k) => {
    const p = parseRecordKey(key);
    const q = parseRecordKey(k);
    if (p.kind === "category") return q.kind === "group" && q.category === p.category;
    return false;
  };
  const kept = (prevExcluded || []).map(String).filter((k) => k !== key && !inside(k));
  return on ? kept : [...kept, key];      // on = ticked again, so no longer excluded
}

// An exclusion only means something while something above it is ticked.
// Untick the parent and the exclusions under it go with it, or they would
// sit there invisibly and subtract from the next selection.
export function pruneExclusions(excluded, checked) {
  const set = new Set((checked || []).map(String));
  const covers = (k) => {
    const p = parseRecordKey(k);
    if (p.kind === "group") return set.has(`${RC}${p.category}`) || set.has(k);
    if (p.kind === "category") return set.has(k);
    // An element key does not say which branch it is under, so it is kept
    // while anything is ticked and goes when nothing is: the server
    // subtracts it from whatever the selection turns out to be, and an
    // exclusion with no selection subtracts from nothing anyway.
    if (p.kind === "element") return set.size > 0;
    return false;
  };
  return (excluded || []).map(String).filter(covers);
}

// The dropped rows, in the keys the SERVER subtracts with
// (utils/selectionExclusions): a record group is 'rg:C|G', a record
// category 'rc:C', an element 'element:<uid>'. They are already written
// that way, so this is only a filter - but it is the one place that says
// so, and the server's parser and this must agree.
export const exclusionKeys = (excluded) => (excluded || []).map(String).filter((k) => (
  k.startsWith(RC) || k.startsWith(RG) || k.startsWith(RS) || k.startsWith("element:")
));

// What the bulk bar sends the server: one node per ticked row. Elements go
// as ids, categories and groups as record nodes, so the branch is resolved
// where the data is rather than from what the browser has loaded.
export function nodesForKeys(keys) {
  const recordNodes = [];
  const uniqueIds = [];
  // SEPARATE, NOT FOLDED IN. A custom category cannot become a recordNode -
  // the server turns those into a match on the record tree and there is no
  // record row for a definition - so it travels as its own id and the server
  // resolves it to part ids.
  const derivedIds = [];
  // Ticks may be RESTORED (a saved selection): one that no longer parses is
  // skipped and named, never thrown and never silently dropped.
  const skipped = [];
  for (const k of minimalRecordKeys(keys)) {
    const p = parseRecordKey(k);
    if (p.kind === "category") recordNodes.push({ category: p.category });
    else if (p.kind === "group" && p.sized) recordNodes.push({ category: p.category, material: p.material, size: p.size });
    else if (p.kind === "group") recordNodes.push({ category: p.category, group: p.group });
    else if (p.kind === "element") uniqueIds.push(p.uniqueId);
    else if (p.kind === "derived" && p.derivedId) derivedIds.push(p.derivedId);
    else skipped.push({ key: k, reason: new UnknownKeyError(k).message });
  }
  return { recordNodes, uniqueIds, derivedIds, skipped };
}
